import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Collection.module.css';
import GlobalHeader from '../GlobalHeader';
import { playUISound } from '../../utils/UISoundEffects';

interface PieceCustomization {
  design: 'tails-standard' | 'heads-standard' | 'tails-8bit' | 'heads-8bit' | 'tails-scribble' | 'heads-scribble';
  material: 'default' | 'metallic' | 'wood' | 'glass';
  colors: ('red' | 'blue' | 'yellow' | 'dark' | 'light')[]; // up to 3
}

interface InventoryItem {
  id: string;
  type: 'design' | 'material' | 'color';
  name: string;
  count?: number; // For colors
  icon?: string;
  owned: boolean;
}

// Alphabetical order for display
const pieceTypes = [
  'bear', 'crane', 'frog', 'goat', 'monkey',
  'fungi', 'octopus', 'penguin', 'rhino',
  'snake', 'tree', 'virus', 'wolf'
].sort();

function Collection() {
  const navigate = useNavigate();
  const [selectedPiece, setSelectedPiece] = useState<{ type: string; color: 'light' | 'dark' } | null>(null);
  const [customization, setCustomization] = useState<PieceCustomization>({
    design: 'tails-standard',
    material: 'default',
    colors: []
  });
  const [showCustomizeModal, setShowCustomizeModal] = useState<boolean>(false);
  const [draggedItem, setDraggedItem] = useState<InventoryItem | null>(null);

  // Mock inventory data - in real app this would come from user profile/database
  const inventory = {
    designs: [
      { id: 'tails-standard', type: 'design' as const, name: 'Standard Tails', owned: true, icon: '🎴' },
      { id: 'heads-standard', type: 'design' as const, name: 'Standard Heads', owned: false, icon: '🎭' },
      { id: 'tails-8bit', type: 'design' as const, name: '8-Bit Tails', owned: false, icon: '🎮' },
      { id: 'heads-8bit', type: 'design' as const, name: '8-Bit Heads', owned: false, icon: '👾' },
      { id: 'tails-scribble', type: 'design' as const, name: 'Scribble Tails', owned: false, icon: '✏️' },
      { id: 'heads-scribble', type: 'design' as const, name: 'Scribble Heads', owned: false, icon: '🖍️' },
    ],
    materials: [
      { id: 'default', type: 'material' as const, name: 'Default', owned: true, icon: '⚪' },
      { id: 'metallic', type: 'material' as const, name: 'Metallic', owned: true, icon: '🔘' },
      { id: 'wood', type: 'material' as const, name: 'Wood', owned: false, icon: '🟫' },
      { id: 'glass', type: 'material' as const, name: 'Glass', owned: false, icon: '💎' },
    ],
    colors: [
      { id: 'red', type: 'color' as const, name: 'Red', count: 5, owned: true, icon: '🔴' },
      { id: 'blue', type: 'color' as const, name: 'Blue', count: 3, owned: true, icon: '🔵' },
      { id: 'yellow', type: 'color' as const, name: 'Yellow', count: 7, owned: true, icon: '🟡' },
      { id: 'dark', type: 'color' as const, name: 'Dark Tint', count: 2, owned: true, icon: '⚫' },
      { id: 'light', type: 'color' as const, name: 'Light Tint', count: 4, owned: true, icon: '⚪' },
    ]
  };

  const handlePieceClick = (type: string, color: 'light' | 'dark') => {
    setSelectedPiece({ type, color });
    // Load current customization for this piece (would come from saved data)
    setCustomization({
      design: 'tails-standard',
      material: 'default',
      colors: []
    });
    setShowCustomizeModal(true);
  };

  const handleDragStart = (item: InventoryItem) => {
    if (!item.owned) return;
    setDraggedItem(item);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem) return;

    if (draggedItem.type === 'design') {
      setCustomization({ ...customization, design: draggedItem.id as any });
    } else if (draggedItem.type === 'material') {
      setCustomization({ ...customization, material: draggedItem.id as any });
    } else if (draggedItem.type === 'color') {
      if (customization.colors.length < 3) {
        setCustomization({
          ...customization,
          colors: [...customization.colors, draggedItem.id as any]
        });
      }
    }

    setDraggedItem(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleRemoveColorSlot = (index: number) => {
    const newColors = [...customization.colors];
    newColors.splice(index, 1);
    setCustomization({
      ...customization,
      colors: newColors
    });
  };

  const handleApplyCustomization = () => {
    // TODO: Save customization to user profile
    // TODO: Deduct colors from inventory
    alert('Customization applied! (This will save to your profile)');
    setShowCustomizeModal(false);
  };

  const handleWashPiece = () => {
    if (window.confirm('Are you sure? This will remove all applied colors (they will be destroyed). Material will remain!')) {
      setCustomization({
        ...customization,
        colors: []
      });
      alert('Piece washed! Colors removed but material remains.');
    }
  };

  const getColorMixPreview = () => {
    const colors = customization.colors;
    if (colors.length === 0) return 'transparent';

    // Simple color mixing logic - can be refined
    const colorMap = {
      red: [255, 0, 0],
      blue: [0, 0, 255],
      yellow: [255, 255, 0],
      dark: [0, 0, 0],
      light: [255, 255, 255]
    };

    let r = 0, g = 0, b = 0;
    colors.forEach(color => {
      const [cr, cg, cb] = colorMap[color];
      r += cr;
      g += cg;
      b += cb;
    });

    // Average the colors
    r = Math.floor(r / colors.length);
    g = Math.floor(g / colors.length);
    b = Math.floor(b / colors.length);

    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div className={styles.container}>
      <GlobalHeader />

      <div className={styles.collectionContainer}>
        <div className={styles.header}>
          <button
            className={styles.backToMain}
            onClick={() => { playUISound(); navigate('/'); }}
          >
            ← Back to Main
          </button>
          <h1 className={styles.title}>My Collection</h1>
          <div className={styles.bagIcon}>🎒</div>
        </div>

        <p className={styles.subtitle}>
          Your personal token bag! Customize each of your 26 pieces with designs, colors, and materials.
        </p>

        {/* Piece Grid - Full Width */}
        <div className={styles.piecesSection}>
            <div className={styles.diceBagHeader}>
              <div className={styles.diceBagIcon}>🎲</div>
              <h3>Your Token Bag</h3>
              <div className={styles.pieceCount}>26 Pieces</div>
            </div>

            <div className={styles.pieceGrid}>
              {/* All Light pieces first */}
              {pieceTypes.map(type => {
                const displayName = type.charAt(0).toUpperCase() + type.slice(1);
                return (
                  <div
                    key={`${type}-light`}
                    className={`${styles.pieceCard} ${
                      selectedPiece?.type === type && selectedPiece?.color === 'light'
                        ? styles.pieceCardSelected
                        : ''
                    }`}
                    onClick={() => handlePieceClick(type, 'light')}
                  >
                    <img
                      src={`/Icons/${displayName}-light.png`}
                      alt={`${type} light`}
                      className={styles.pieceImage}
                    />
                    <div className={styles.pieceName}>
                      Light {displayName}
                    </div>
                  </div>
                );
              })}

              {/* Divider between light and dark */}
              <div className={styles.pieceDivider}></div>

              {/* All Dark pieces second */}
              {pieceTypes.map(type => {
                const displayName = type.charAt(0).toUpperCase() + type.slice(1);
                return (
                  <div
                    key={`${type}-dark`}
                    className={`${styles.pieceCard} ${
                      selectedPiece?.type === type && selectedPiece?.color === 'dark'
                        ? styles.pieceCardSelected
                        : ''
                    }`}
                    onClick={() => handlePieceClick(type, 'dark')}
                  >
                    <img
                      src={`/Icons/${displayName}-dark.png`}
                      alt={`${type} dark`}
                      className={styles.pieceImage}
                    />
                    <div className={styles.pieceName}>
                      Dark {displayName}
                    </div>
                  </div>
                );
              })}
            </div>
        </div>

        {/* Customization Modal */}
        {showCustomizeModal && selectedPiece && (
          <div className={styles.modalOverlay} onClick={() => setShowCustomizeModal(false)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <button
                className={styles.modalCloseButton}
                onClick={() => setShowCustomizeModal(false)}
              >
                ✕
              </button>

              <h2 className={styles.modalTitle}>
                {selectedPiece.color.charAt(0).toUpperCase() + selectedPiece.color.slice(1)} {selectedPiece.type.charAt(0).toUpperCase() + selectedPiece.type.slice(1)}
              </h2>

              {/* Main Layout: Preview on left, Inventory on right */}
              <div className={styles.inventoryLayout}>
                {/* Left: Large Preview with Drop Zone */}
                <div className={styles.previewColumn}>
                  <div className={styles.previewLabel}>Drag items onto your piece!</div>
                  <div
                    className={styles.modalPreviewBox}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <img
                      src={`/Icons/${selectedPiece.type.charAt(0).toUpperCase() + selectedPiece.type.slice(1)}-${selectedPiece.color}.png`}
                      alt="preview"
                      className={styles.modalPreviewImage}
                      style={{
                        filter: customization.colors.length > 0
                          ? `brightness(1.2) contrast(1.1)`
                          : 'none',
                        backgroundColor: getColorMixPreview(),
                        padding: '20px',
                        borderRadius: '15px'
                      }}
                    />
                  </div>

                  {/* Applied Customizations */}
                  <div className={styles.appliedItems}>
                    <div className={styles.appliedItem}>
                      <span className={styles.appliedLabel}>Design:</span>
                      <span className={styles.appliedValue}>
                        {inventory.designs.find(d => d.id === customization.design)?.name || 'None'}
                      </span>
                    </div>
                    <div className={styles.appliedItem}>
                      <span className={styles.appliedLabel}>Material:</span>
                      <span className={styles.appliedValue}>
                        {inventory.materials.find(m => m.id === customization.material)?.name || 'None'}
                      </span>
                    </div>
                    <div className={styles.appliedItem}>
                      <span className={styles.appliedLabel}>Colors:</span>
                      <span className={styles.appliedValue}>
                        {customization.colors.length > 0 ? customization.colors.map(c =>
                          inventory.colors.find(col => col.id === c)?.icon
                        ).join(' ') : 'None'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.applyButton}
                      onClick={handleApplyCustomization}
                    >
                      ✓ Apply to Piece
                    </button>
                    <button
                      className={styles.washButton}
                      onClick={handleWashPiece}
                    >
                      💧 Wash Colors Only
                    </button>
                  </div>
                </div>

                {/* Right: Inventory Panel */}
                <div className={styles.inventoryColumn}>
                  <h3 className={styles.inventoryTitle}>Your Inventory</h3>

                  {/* Designs Section */}
                  <div className={styles.inventorySection}>
                    <h4 className={styles.inventorySectionTitle}>
                      🎴 Designs <span className={styles.inventoryHint}>(Tails = Side view, Heads = Face view)</span>
                    </h4>
                    <div className={styles.inventoryGrid}>
                      {inventory.designs.map(item => (
                        <div
                          key={item.id}
                          className={`${styles.inventoryItem} ${
                            !item.owned ? styles.inventoryItemLocked : ''
                          } ${
                            customization.design === item.id ? styles.inventoryItemActive : ''
                          }`}
                          draggable={item.owned}
                          onDragStart={() => handleDragStart(item)}
                          onDragEnd={handleDragEnd}
                          onClick={() => item.owned && setCustomization({ ...customization, design: item.id as any })}
                        >
                          <div className={styles.inventoryItemIcon}>{item.icon}</div>
                          <div className={styles.inventoryItemName}>{item.name}</div>
                          {!item.owned && <div className={styles.lockedOverlay}>🔒</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Materials Section */}
                  <div className={styles.inventorySection}>
                    <h4 className={styles.inventorySectionTitle}>
                      🔘 Materials <span className={styles.inventoryHint}>(Permanent when applied!)</span>
                    </h4>
                    <div className={styles.inventoryGrid}>
                      {inventory.materials.map(item => (
                        <div
                          key={item.id}
                          className={`${styles.inventoryItem} ${
                            !item.owned ? styles.inventoryItemLocked : ''
                          } ${
                            customization.material === item.id ? styles.inventoryItemActive : ''
                          }`}
                          draggable={item.owned}
                          onDragStart={() => handleDragStart(item)}
                          onDragEnd={handleDragEnd}
                          onClick={() => item.owned && setCustomization({ ...customization, material: item.id as any })}
                        >
                          <div className={styles.inventoryItemIcon}>{item.icon}</div>
                          <div className={styles.inventoryItemName}>{item.name}</div>
                          {!item.owned && <div className={styles.lockedOverlay}>🔒</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Colors Section */}
                  <div className={styles.inventorySection}>
                    <h4 className={styles.inventorySectionTitle}>
                      🎨 Color Pots <span className={styles.inventoryHint}>(Mix up to 3 colors!)</span>
                    </h4>
                    <div className={styles.inventoryGrid}>
                      {inventory.colors.map(item => (
                        <div
                          key={item.id}
                          className={`${styles.inventoryItem} ${
                            !item.owned ? styles.inventoryItemLocked : ''
                          } ${
                            customization.colors.includes(item.id as any) ? styles.inventoryItemUsed : ''
                          }`}
                          draggable={item.owned && customization.colors.length < 3}
                          onDragStart={() => handleDragStart(item)}
                          onDragEnd={handleDragEnd}
                          onClick={() => {
                            if (item.owned && customization.colors.length < 3 && !customization.colors.includes(item.id as any)) {
                              setCustomization({
                                ...customization,
                                colors: [...customization.colors, item.id as any]
                              });
                            }
                          }}
                        >
                          <div className={styles.inventoryItemIcon}>{item.icon}</div>
                          <div className={styles.inventoryItemName}>{item.name}</div>
                          <div className={styles.inventoryItemCount}>×{item.count}</div>
                          {!item.owned && <div className={styles.lockedOverlay}>🔒</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Color Slots Display */}
                  {customization.colors.length > 0 && (
                    <div className={styles.colorSlotsDisplay}>
                      <h5>Applied Colors:</h5>
                      <div className={styles.appliedColorsList}>
                        {customization.colors.map((colorId, index) => {
                          const colorItem = inventory.colors.find(c => c.id === colorId);
                          return (
                            <div key={index} className={styles.appliedColorChip}>
                              <span>{colorItem?.icon} {colorItem?.name}</span>
                              <button
                                className={styles.removeColorBtn}
                                onClick={() => handleRemoveColorSlot(index)}
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <div className={styles.mixedColorPreview}>
                        <span>Mixed Result:</span>
                        <div
                          className={styles.mixedColorBox}
                          style={{ backgroundColor: getColorMixPreview() }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Collection;
